/* eslint-disable @typescript-eslint/ban-ts-comment */
import React, { useRef, useMemo } from 'react';
import { ColumnDef } from '@tanstack/react-table';
import { useNavigate } from 'react-router';
import { html } from 'lit/static-html.js';
import {
  Button,
  DataTable,
  DataTableWC,
  Grid,
  GridCell,
  Icon,
  PageSection,
  Search,
  Select,
  SelectOption,
  Spacer,
  Stack,
  TextTitle,
} from '@pypestream/design-system';
import { TranslationComponent, useTranslation } from '@pypestream/translations';
// import { formatTimeWithTimezone } from '@pypestream/utils';

import { useCreateSolutionModal } from '../components';
import {
  sendProStudioEvent,
  useProStudioCtxSelector,
  useProStudioStateMatches,
} from '../xstate/app.xstate';
import { WithLoadingState } from '../router';
import { StudioPageProps, useProStudioViewerRole } from '../hooks';
import { buildSolutionHref, isPlainLeftClick } from '../utils/solution-link';

interface HardcodedSolutionType {
  id?: string;
  name: string;
  first_name: string;
  last_name: string;
  profilePhoto: string;
  updated: string;
}

type HomePageProps = StudioPageProps;

const HomePage: React.FC<HomePageProps> = () => {
  const tableRef = useRef<DataTableWC>(null);

  const { bots, projectId, userProjects } = useProStudioCtxSelector((ctx) => ({
    projectId: ctx.project?.id,
    bots: (ctx.solutions || [])
      .map((bot) => ({
        id: bot.id,
        name: bot.displayName,
        // HARDCODED DATA FOR NOW
        first_name: 'Firstname',
        last_name: 'Lastname',
        profilePhoto:
          'https://images.pexels.com/photos/2216542/pexels-photo-2216542.jpeg?auto=compress&amp;cs=tinysrgb&amp;w=100&amp;h=100&amp;dpr=2',
        updated: '2025-01-14T13:13:17.986Z',
      }))
      // @todo - move this sorting to gql query, currently we don't have ability to add sorting to the getSolutions query so doing it here
      .sort((a, b) => (a.name || '').localeCompare(b.name || '')),
    userProjects: ctx.userProjects,
  }));

  const isViewer = useProStudioViewerRole();

  const { openModal, createSolutionModal } = useCreateSolutionModal();
  const [t] = useTranslation();
  const navigate = useNavigate();
  const isIdle = useProStudioStateMatches(
    'checkingUserInfo.projectFlow.projectDataLoaded.idle'
  );
  // PSTUDIO-3739: when the open solution is deleted (e.g. swept by concurrent
  // E2E), the machine ejects to the parked terminal state `solutionNotFound` and
  // the router redirects here to All Solutions — but the machine stays parked
  // there (NOT `projectDataLoaded.idle`). This page's actions (Create Solution)
  // must stay enabled in that parked state too, otherwise a delete→redirect
  // leaves the "Create Solution" button permanently disabled and the user stuck.
  const isSolutionNotFound = useProStudioStateMatches('solutionNotFound');

  const columns = useMemo<ColumnDef<HardcodedSolutionType>[]>(
    () => [
      {
        accessorKey: 'name',
        accessorFn: (row) => row.name,
        header: () =>
          t('pro-studio/homepage:table.column1', {
            defaultValue: 'Solution Title',
          }) || '',
        cell: ({ row }) => {
          const href = buildSolutionHref(projectId ?? '', row.original.id);
          return html` <a
            href=${href}
            style="display:block;color:inherit;text-decoration:none;"
            @click=${(e: MouseEvent) => {
              // Only a plain left-click drives SPA navigation. Any modified click (cmd/ctrl/shift/
              // alt) must reach the browser's native new-tab/new-window handling, so we stop it
              // from bubbling to the row @click (which would SPA-navigate the CURRENT tab) but must
              // NOT preventDefault. Real middle/right-clicks fire auxclick/contextmenu (never
              // click), so they never reach this handler — the native <a href> opens them itself.
              if (!isPlainLeftClick(e)) {
                e.stopPropagation();
                return;
              }
              // Plain left-click → block the anchor's own navigation; the existing ps-table-row
              // @click (toggleSelected → selectBot → navigate) drives SPA navigation.
              e.preventDefault();
            }}
          >
            <ps-text-subtitle size="small"
              >${row?.original?.name}</ps-text-subtitle
            >
          </a>`;
        },
      },
      // {
      //   accessorKey: 'userName',
      //   header: () =>
      //     t('pro-studio/homepage:table.column2', {
      //       defaultValue: 'Last updated',
      //     }) || '',
      //   cell: ({ row }) => {
      //     if (!row || !row?.original) {
      //       return null;
      //     }

      //     const { first_name, last_name, profilePhoto, updated } = row.original;
      //     const userName = `${first_name} ${last_name}`;

      //     return html`<ps-stack
      //       alignItems="center"
      //       justifyContent="end"
      //       gutter="xsmall"
      //     >
      //       <ps-text-body>
      //         ${formatTimeWithTimezone({
      //           fromDate: updated,
      //           // locale: dateFnsLocales[defaultLanguage || fallbackLanguage],
      //           // userTimeZone,
      //         })}
      //         ${' '}by
      //       </ps-text-body>
      //       <ps-tooltip content=${userName}>
      //         <ps-avatar
      //           size="small"
      //           src=${profilePhoto}
      //           label=${userName}
      //         ></ps-avatar>
      //       </ps-tooltip>
      //     </ps-stack>`;
      //   },
      //   meta: {
      //     align: 'end',
      //   },
      // },
    ],
    [projectId, t]
  );

  return projectId ? (
    <React.Fragment>
      <PageSection test-id="ps-homepage-page-section">
        <Grid alignItems="center">
          <GridCell xsmall="12" large="5">
            <TextTitle test-id="ps-homepage-title" size="xsmall">
              <TranslationComponent i18nKey="pro-studio/homepage:title">
                Solutions
              </TranslationComponent>
            </TextTitle>
          </GridCell>
          <GridCell xsmall="12" large="7">
            <Stack alignItems="center" justifyContent="end">
              <Search
                test-id="ps-homepage-search"
                size="large"
                name="search"
                style={{ width: 319 }}
                onInput={(e) => {
                  tableRef.current?.table.setGlobalFilter(
                    String(e.target?.value) || ''
                  );
                }}
              />
              {!isViewer && (
                <Button
                  test-id="ps-homepage-create-solution-btn"
                  size="large"
                  onClick={openModal}
                  disabled={!isIdle && !isSolutionNotFound}
                >
                  <Icon name="add" slot="prefix" />
                  <TranslationComponent i18nKey="pro-studio/homepage:create">
                    Create Solution
                  </TranslationComponent>
                </Button>
              )}
            </Stack>
          </GridCell>
        </Grid>

        <Spacer size="large" />

        <DataTable
          test-id="ps-homepage-solutions-table"
          ref={tableRef}
          filterColumns={['name']}
          size="small"
          // @ts-ignore
          columns={columns}
          data={bots}
          enable-row-selection
          onRowSelectionChange={(e: CustomEvent) => {
            const botName = e.detail.selected[0]?.original.name;
            const botId = e.detail.selected[0]?.original.id;

            if (!botName || !botId) {
              return;
            }

            sendProStudioEvent({
              type: 'selectBot',
              name: botName,
              id: botId,
              cb: (path?: string) => navigate(path || ''),
            });
          }}
        />
      </PageSection>
      {createSolutionModal}
    </React.Fragment>
  ) : (
    <Stack
      gutter="xsmall"
      justifyContent="center"
      alignItems="center"
      direction="column"
    >
      <TextTitle size="xsmall">
        Please select a project to view the available solutions
      </TextTitle>
      <Spacer size="xsmall" />
      <Select
        placeholder="Select a project"
        onChange={(e) => {
          const selectedProjectId = e.target.value as string;
          sendProStudioEvent({
            type: 'setParams',
            payload: {
              projectId: selectedProjectId,
            },
          });
        }}
      >
        {userProjects?.map((project) => (
          <SelectOption key={project.id} value={project.id}>
            {project.name}
          </SelectOption>
        ))}
      </Select>
    </Stack>
  );
};

export const Home = WithLoadingState(HomePage);
